import { forwardRef, useImperativeHandle, useRef } from 'react';
import classnames from 'clsx';

import style from './style.module.less';
import { useOnComponentMount } from '../../../../hooks/use-on-component-mount';
import { InlineMessage } from '../../inline-message';
import { RequiredIndicator } from '../../required-indicator';

import type { InputVersion2Props, InputVersion2Ref } from './input-version2-types';

/**
 * List of props that should not be passed to the input element
 */
const blackListLookup: Record<string, boolean> = [
  'additionalInfoLabel',
  'label',
  'setRef',
  'isPrivate',
  'hasError',
  'className',
  'inlineMessage',
  'inlineMessageType',
  'inlineMessageTooltip',
  'focusOnMount',
  'withAsterisk',
].reduce(
  (acc, propName) => ({
    ...acc,
    [propName]: true,
  }),
  {},
);

/**
 * Filters out props that should not be passed to the input element
 */
const getFilteredProps = (props: InputVersion2Props): Partial<InputVersion2Props> => {
  return Object.keys(props).reduce((acc, propName) => {
    if (!blackListLookup[propName]) {
      return {
        ...acc,
        [propName]: props[propName as keyof InputVersion2Props],
      };
    }
    return acc;
  }, {});
};

/**
 * InputVersion2 component
 */
export const Input = forwardRef<InputVersion2Ref, InputVersion2Props>((props, ref) => {
  const {
    label,
    additionalInfoLabel,
    disabled = false,
    inlineMessage,
    inlineMessageType,
    inlineMessageTooltip,
    isPrivate = false,
    className = '',
    id = '',
    focusOnMount = false,
    withAsterisk = false,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  useOnComponentMount(() => {
    if (focusOnMount && inputRef.current) {
      inputRef.current.focus();
    }
  });

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [inputRef]);

  const containerClassName = classnames(style.container, className, {
    [style.disabled]: disabled,
    [style[inlineMessageType as string]]: !!inlineMessage && !!inlineMessageType,
    'median-exclude-selector': isPrivate,
  });

  return (
    <div className={containerClassName}>
      {label ? (
        <label className={style.label} htmlFor={id}>
          {label}
          {withAsterisk ? <RequiredIndicator /> : null}
          {additionalInfoLabel ? <span className={style['additional-info-label']}>{additionalInfoLabel}</span> : null}
        </label>
      ) : null}
      <input
        ref={inputRef}
        className={classnames(style.input, {
          [style['without-label']]: !label,
        })}
        {...getFilteredProps(props)}
      />
      {inlineMessage ? (
        <InlineMessage tooltipContent={inlineMessageTooltip} type={inlineMessageType}>
          {inlineMessage}
        </InlineMessage>
      ) : null}
    </div>
  );
});
